import styles from "@styles/pages/reports/reports.module.scss";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";

import classNames from "classnames/bind";
import { Button, Checkbox, Drawer, Tabs } from "@components";
import { ForEach, If } from "shared/components/control-statements";
import TimeFilter from "pages/Reports/TimeFilter";
import { OlgaEnvironmentsAPI } from "providers/OlgaEnvironmentsAPI";
import { InputSearch } from "shared/components/InputSearch";
import { Environment } from "shared/core/Environment";
const css = classNames.bind(styles);

const tabs = {
  PERIOD: {
    key: "Período",
    name: "Período",
  },
  BRANDS_UNITS: {
    key: "Marcas e Unidades",
    name: "Marcas e Unidades",
  },
  ORIGINS: {
    key: "Origens",
    name: "Origens",
  },
  COUPONS: {
    key: "Cupons",
    name: "Cupons",
  },
};

const BrandSection = ({
  brand,
  selectedUnits,
  onToggleUnit,
  onToggleAll,
  hideUnits,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const isSomeChecked = brand.units.reduce(
    (r, u) => r || selectedUnits.includes(u.id),
    false
  );
  const [search, setSearch] = useState("");

  const filteredUnits = Object.values(brand.units).filter((c) => {
    return search ? c.name?.toLowerCase().includes(search.toLowerCase()) : true;
  });

  if (brand.units.length === 0) return;

  return (
    <div className={styles.brand}>
      <div
        className={css({ header: true, collapsed })}
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="d-flex align-center justify-between w-100">
          <Checkbox
            checked={isSomeChecked}
            design="two"
            onChange={() => onToggleAll(!isSomeChecked)}
          >
            {brand?.name}
          </Checkbox>
          <If cond={!hideUnits}>
            <i className="fas fa-chevron-down" />
          </If>
        </div>
      </div>

      {collapsed && !hideUnits && (
        <div className={styles.content}>
          <InputSearch
            placeholder="Buscar por unidade..."
            design="three"
            occurrences={brand.units.length}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className={styles.options}>
            <ForEach
              items={filteredUnits}
              render={(u) => (
                <div
                  className={styles.option}
                  onClick={() => onToggleUnit(u.id)}
                >
                  <Checkbox
                    label={u.name}
                    design="two"
                    checked={selectedUnits.indexOf(u.id) > -1}
                    readOnly
                  />
                </div>
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const ReportFilters = forwardRef(
  (
    {
      open,
      onClose,
      onUpdateFilters,
      filters,
      dateFiltersProp,
      onUpdateDateFilters,
      hideBrands = false,
      hideUnits = false,
      hideOrigins = false,
      showCoupons = false,
      hideHours = false,
      allowOnlyMonth = false,
      withoutTransition = false,
      allowIntervalAfter,
      coupons,
      hideSpecificDay,
      hideSpecificYear,
      ignoreBlockOrigins = true,
    },
    ref
  ) => {
    const [activeTab, setActiveTab] = useState(tabs.PERIOD.key);
    const [selectedFilterType, setSelectedFilterType] = useState(null);
    const [dateFilters, setDateFilters] = useState(null);
    const [filterParams, setFilterParams] = useState({});
    const [brands, setBrands] = useState([]);
    const [units, setUnits] = useState([]);
    const [origins, setOrigins] = useState([]);
    const [selectedOrigins, setSelectedOrigins] = useState([]);
    const [searchOrigins, setSearchOrigin] = useState("");
    const [disableApply, setDisableApply] = useState(false);
    const [selectedCoupons, setSelectedCoupons] = useState([]);
    const [searchCoupon, setSearchCoupon] = useState("");

    useEffect(() => {
      if (filters && Object.values(filters)?.length === 0)
        setDisableApply(true);
      else setDisableApply(false);
    }, [open]);

    useEffect(() => {
      if (filters && Object.values(filters)?.length === 0)
        setDisableApply(true);
      else setDisableApply(false);
      if (filters) {
        setSelectedOrigins(filters.origin || []);
        // setSelectedCoupons(filters.coupon_id || []);
        setUnits([...new Set(filters.unit_id || [])]);
        setSelectedFilterType(filters.period);
      }
    }, [filters]);

    useEffect(() => {
      if (dateFiltersProp) {
        setDateFilters(dateFiltersProp);
      }
    }, [dateFiltersProp]);

    const formatFilterParams = (params) => {
      const payload = {};

      if (params.units) {
        payload.unit_id = params.units;
      }

      if (params.coupons) {
        payload.coupon_id = params.coupons;
      }

      if (params.origins) {
        payload.origin = params.origins;
      }

      if (params.date) {
        const { begin, end } = params.date;
        payload.start_date = begin;
        payload.end_date = end;
        payload.period = selectedFilterType;
      }

      return payload;
    };

    const removeFilter = (filter) => {
      const newFilterParams = { ...filterParams };
      delete newFilterParams[filter];
      setFilterParams({ ...newFilterParams });
      onUpdateFilters(formatFilterParams(newFilterParams));

      if (filter === "date") {
        setDateFilters(null);
        setSelectedFilterType(null);
      }
      if (filter === "origins") setSelectedOrigins([]);
      if (filter === "units") setUnits([]);
      if (filter === "coupons") setSelectedCoupons([]);
    };

    useImperativeHandle(ref, () => ({
      removeFilter,
    }));

    const onChangeFilters = (dateFilters) => {
      const newFilterParams = { ...filterParams };

      for (const [key, value] of Object.entries(dateFilters)) {
        newFilterParams[key] = value;
      }

      setFilterParams({ ...newFilterParams });
    };

    useEffect(() => {
      const loadBrandAndUnits = async () => {
        const [unitResponse, brandsResponse] = await Promise.all([
          OlgaEnvironmentsAPI.getUnits(),
          OlgaEnvironmentsAPI.getBrands(),
        ]);

        const brandList = brandsResponse.getData([]);
        const unitList = unitResponse.getData([]);

        for (const b of brandList) {
          b.units = unitList.filter((f) => f.brand_id == b.id);
        }

        brandList.push({
          name: "Sem marca",
          units: unitList.filter((f) => !f.brand_id),
        });

        setBrands(brandList);
      };

      const olgaOrigins = [
        "Olga Pay",
        "Olga App Takeaway",
        "Olga App Delivery",
        "Olga App Fastfila",
        "Olga Totem Takeaway",
        "Olga Totem Delivery",
        "Olga Totem Fastfila",
      ];

      const fetchOrigins = async () => {
        const originsResponse =
          await OlgaEnvironmentsAPI.getAllAvailableExternalSourcesConfigs();
        const origins = originsResponse.getData([]);

        const environment = Environment.configurations;
        const reports_allow_origin = !environment.reports_allow_origin;

        setOrigins(
          origins
            .filter((item) => {
              if (reports_allow_origin && !ignoreBlockOrigins)
                return olgaOrigins.includes(item.external_source);

              return item.external_source;
            })
            .map((item) => ({
              id: item.external_source,
              name: item.external_source,
            }))
        );
      };

      if (open) {
        fetchOrigins();
        setOrigins(origins);

        loadBrandAndUnits();
      }
    }, [open]);

    const filteredOrigins = origins.filter((o) => {
      return searchOrigins
        ? o.name.toLowerCase().includes(searchOrigins.toLowerCase())
        : true;
    });

    const filteredCoupons = (coupons || []).filter((o) => {
      return searchCoupon
        ? o.code.toLowerCase().includes(searchCoupon.toLowerCase())
        : true;
    });

    const applyFilters = () => {
      onClose();
      onUpdateFilters(formatFilterParams(filterParams));
    };

    useEffect(() => {
      onChangeFilters({
        units,
        origins: selectedOrigins,
        coupons: selectedCoupons,
      });
    }, [units, selectedOrigins, selectedCoupons]);

    const filteredTabs = Object.values(tabs).filter((t) => {
      if (hideBrands && t.key === tabs.BRANDS_UNITS.key) return false;
      if (hideOrigins && t.key === tabs.ORIGINS.key) return false;
      if (!showCoupons && t.key === tabs.COUPONS.key) return false;
      return true;
    });

    return (
      <Drawer
        visible={open}
        onClose={() => onClose()}
        title="Filtrar"
        withoutTransition={withoutTransition}
        showBackButton
        backButton={onClose}
      >
        {filteredTabs.length > 1 && (
          <Tabs
            full
            tabs={filteredTabs}
            onTabSelected={(tab) => setActiveTab(tab)}
            activeTab={activeTab}
          />
        )}

        <div className={styles.reportFilters}>
          <If cond={activeTab === tabs.PERIOD.key}>
            <TimeFilter
              onUpdateFilters={(dateFilters) => {
                onChangeFilters({ date: dateFilters });
              }}
              onUpdateFilterValues={(values, selectedFilterType) => {
                setSelectedFilterType(selectedFilterType);
                setDateFilters(values);
                onUpdateDateFilters(values);
              }}
              values={dateFilters}
              filterType={selectedFilterType}
              allowOnlyMonth={allowOnlyMonth}
              allowIntervalAfter={allowIntervalAfter}
              onDisableApply={(status) => setDisableApply(status)}
              hideHours={hideHours}
              hideSpecificDay={hideSpecificDay}
              hideSpecificYear={hideSpecificYear}
            />
          </If>

          <If cond={activeTab === tabs.BRANDS_UNITS.key}>
            <div className={styles.brands}>
              <ForEach
                items={brands}
                render={(b) => {
                  return (
                    <BrandSection
                      brand={b}
                      selectedUnits={units}
                      onToggleUnit={(unitId) => {
                        if (units.indexOf(unitId) > -1) {
                          setUnits(units.filter((u) => u != unitId));
                        } else {
                          setUnits([...units, unitId]);
                        }
                      }}
                      hideUnits={hideUnits}
                      onToggleAll={(checkAll) => {
                        const unitIds = b.units.map((u) => u.id);
                        if (checkAll) {
                          setUnits([...new Set([...units, ...unitIds])]);
                        } else {
                          setUnits(
                            units.filter((u) => unitIds.indexOf(u) == -1)
                          );
                        }
                      }}
                    />
                  );
                }}
              />
            </div>
          </If>

          <If cond={activeTab === tabs.ORIGINS.key}>
            <InputSearch
              placeholder="Buscar por origem..."
              design="three"
              className="mb-10"
              occurrences={origins.length}
              onChange={(e) => setSearchOrigin(e.target.value)}
            />
            <div className={styles.origins}>
              <ForEach
                items={filteredOrigins}
                render={(origin) => {
                  return (
                    <div
                      className={css({
                        origin: true,
                        selected: selectedOrigins.indexOf(origin.id) > -1,
                      })}
                      onClick={() => {
                        if (selectedOrigins.indexOf(origin.id) > -1) {
                          setSelectedOrigins(
                            selectedOrigins.filter((u) => u !== origin.id)
                          );
                        } else {
                          setSelectedOrigins([...selectedOrigins, origin.id]);
                        }
                      }}
                    >
                      <Checkbox
                        checked={selectedOrigins.indexOf(origin.id) > -1}
                        design="two"
                        readOnly
                      >
                        {origin?.name}
                      </Checkbox>
                    </div>
                  );
                }}
              />
            </div>
          </If>

          <If cond={activeTab === tabs.COUPONS.key}>
            <InputSearch
              placeholder="Buscar por cupom..."
              design="three"
              className="mb-10"
              occurrences={coupons?.length}
              onChange={(e) => setSearchCoupon(e.target.value)}
            />
            <div className={styles.origins}>
              <ForEach
                items={filteredCoupons}
                render={(coupon) => {
                  return (
                    <div
                      className={css({
                        origin: true,
                        selected: selectedCoupons.indexOf(coupon.id) > -1,
                      })}
                      onClick={() => {
                        if (selectedCoupons.indexOf(coupon.id) > -1) {
                          setSelectedCoupons(
                            selectedCoupons.filter((u) => u !== coupon.id)
                          );
                        } else {
                          setSelectedCoupons([...selectedCoupons, coupon.id]);
                        }
                      }}
                    >
                      <Checkbox
                        checked={selectedCoupons.indexOf(coupon.id) > -1}
                        design="two"
                        readOnly
                      >
                        {coupon?.code}
                      </Checkbox>
                    </div>
                  );
                }}
              />
            </div>
          </If>

          <div className={styles.reportFiltersFooter}>
            <Button
              design="primary"
              className="w-100"
              onClick={applyFilters}
              disabled={disableApply}
            >
              Aplicar
            </Button>
          </div>
        </div>
      </Drawer>
    );
  }
);

export default ReportFilters;
